import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "@routing/routes";
import { useAtomValue } from "jotai";
import { Spinner, ErrorState } from "@common/index";
import { useShoppingCartApi } from "@/hooks/apiHooks/useShoppingCartApi";
import { useVendorApi } from "@/hooks/apiHooks/useVendorApi";
import { useUserApi } from "@/hooks/apiHooks/useUserApi";
import { useMountRead } from "@/hooks/useMountRead";
import {
    isProcessingShoppingCartAtom,
    shoppingCartAtom,
} from "@/store/shoppingCartAtoms";
import { shippingPolicyAtom } from "@/store/shippingAtoms";
import { hasNoLocationMembershipAtom } from "@/store/shipToAtoms";
import { NoLocationNote } from "@customer/_components/NoLocationNote";
import {
    NO_LOCATION_CART_NOTE,
    NO_LOCATION_ORDER_BLOCKED,
    excludedCaveat,
} from "@customer/_helpers/noLocationCopy";
import { currencyFormatter } from "@utils/formatter";
import { getProductImage } from "@utils/productImage";
import { ProductCarouselRow } from "@customer/Browse/components/ProductCarouselRow";
import type { ProductCardData, ProductVendor } from "@models/product";
import type { ShoppingCartItem } from "@models/shoppingCart";
import { isCardPaymentConfigured } from "@/config/paymentConfig";
import { RestrictedLinesNotice } from "../_components/RestrictedLinesNotice";
import { CHECKOUT_UNAVAILABLE_REASON } from "../_helpers/checkoutUnavailableCopy";
import { outOfStockReason, overStockReason } from "../_helpers/stockCopy";
import { quantityCap } from "../_helpers/quantityCap";
import { useCartProducts } from "../_helpers/useCartProducts";
import { linePiecesPerUnit } from "../_helpers/linePiecesPerUnit";
import {
    restrictedLineCount,
    shippableSubtotal,
    shippableUnitCount,
} from "../_helpers/cartTotals";
import { CartItemRow } from "./CartItemRow";
import { OrderSummary } from "./OrderSummary";
import {
    reshapeVendorSkus,
    selectMostFrequentVendor,
} from "./_helpers/cartRails";
import { useCartItemMutations } from "./_helpers/useCartItemMutations";
import { useRelatedRail } from "./_helpers/useRelatedRail";
import {
    alternativeVendor,
    cartStock,
    limitedAlternative,
} from "./_helpers/lineStock";
import {
    lineHasNoOffers,
    noOfferLineCount,
    offeredLines,
} from "./_helpers/lineOffers";
import {
    refusalNextStep,
    type RefusalNextStep,
} from "./_helpers/refusalNextStep";
import { useVendorSwitch } from "./_helpers/useVendorSwitch";
import { LineStockNotice } from "./_components/LineStockNotice";
import {
    LineRefusalNotice,
    type LineRefusalAction,
} from "./_components/LineRefusalNotice";

// Both rails show at most six products; getProductsByVendor over-fetches so
// that after dedupe-to-product-grain and cart exclusion six usually remain.
const VENDOR_RAIL_FETCH_LIMIT = 30;

export const Cart = () => {
    const { getShoppingCart } = useShoppingCartApi();
    const { getProductsByVendor } = useVendorApi();
    const {
        changeQuantity,
        removeItem,
        hasPendingEdits,
        quantityRefusals,
        clearQuantityRefusal,
    } = useCartItemMutations();
    const { switchVendor, switchingItemId, refusals, clearRefusal } =
        useVendorSwitch();
    // Any cart write in flight — or a quantity edit still armed behind its debounce —
    // holds a vendor switch back: the PUT can merge into a line whose trailing PATCH
    // would then SET it back to the edited number, dropping the merged units. The same
    // settling rule Checkout's Place Order keeps (isCartSettling).
    const isCartMutating = useAtomValue(isProcessingShoppingCartAtom);
    const isCartSettling = hasPendingEdits || isCartMutating;
    const isSwitchHeld = switchingItemId !== null || isCartSettling;
    // The one vendor switch, offered by a line above stock or by a line its vendor no
    // longer offers. Once switched, the line is with another vendor: its quantity refusal
    // is moot.
    const switchLine = (item: ShoppingCartItem, vendor: ProductVendor) =>
        void switchVendor(item, vendor).then((switched) => {
            if (switched) clearQuantityRefusal(item.id);
        });
    const shoppingCart = useAtomValue(shoppingCartAtom);
    // The status of the cart read THIS page issued (#407), never app-wide
    // atoms: those outlive the page, so with no cart in hand a revisit painted
    // the last visit's failure, and a first visit painted "Your cart is empty."
    // before the cart had been read. The read may join one already in flight
    // (the header badge's); its outcome is still this page's. Wrapped so the
    // `isCurrent` useMountRead hands a read never reaches getShoppingCart as
    // its options.
    const readCart = useCallback(() => getShoppingCart(), [getShoppingCart]);
    const { readStatus, reload: fetchCart } = useMountRead(readCart);
    const shippingPolicy = useAtomValue(shippingPolicyAtom);
    // #417. The cart payload says only THAT a line is restricted, never why
    // (ShoppingCartItem.isRestricted; no reason on the wire), so the one case
    // where "ship to a different location" is the wrong advice — a member
    // seated on no location, whose legal-class lines all fail closed — is keyed
    // on the status the API reports for exactly that member instead.
    const hasNoLocationMembership = useAtomValue(hasNoLocationMembershipAtom);
    // That status is otherwise read once per session (ProtectedRoute), and the
    // admin may have assigned a location since. With no reason on the cart's
    // lines to go by, the cart re-reads /users/me on mount so its copy is not
    // a session old. ApprovalGate renders the page whenever a user is present,
    // so the refresh never unmounts this page; a failure keeps the last status.
    const { getMeUser } = useUserApi();
    useEffect(() => {
        getMeUser().catch(() => {
            /* the notification layer surfaces errors; the last status stands */
        });
    }, [getMeUser]);
    const [vendorRail, setVendorRail] = useState<ProductCardData[]>([]);
    const [isLoadingVendor, setIsLoadingVendor] = useState(false);

    const items = useMemo(() => shoppingCart?.items ?? [], [shoppingCart]);
    const mostFrequentVendor = useMemo(
        () => selectMostFrequentVendor(items),
        [items],
    );
    const mostFrequentVendorId = mostFrequentVendor?.vendorId ?? null;

    // Stable rail-effect keys. Keying the fetch effects on these derived values
    // (not the `items` array identity) stops a pure quantity +/- change from
    // refiring the related/vendor fetches: the product set and the winning
    // vendor are unchanged by a quantity edit, so their keys are unchanged.
    const relatedProductIdsKey = useMemo(
        () =>
            Array.from(new Set(items.map((i) => i.productId)))
                .sort()
                .join(","),
        [items],
    );

    // The vendor effect reads current items (for cart-sku exclusions) without
    // depending on their identity; a ref keeps that read fresh across renders.
    const itemsRef = useRef(items);
    itemsRef.current = items;

    // Related Items rail: one read for every distinct cart product (#449).
    const relatedRail = useRelatedRail(relatedProductIdsKey);

    // Shop {vendor} rail: fetch the most-frequent vendor's other SKUs and
    // reshape SKU rows to product-grain cards, excluding cart products.
    useEffect(() => {
        // Null only for an empty cart, where the rail is gated off in render.
        if (mostFrequentVendorId === null) {
            return;
        }
        let cancelled = false;
        // Read current items via the ref so the sku exclusions stay fresh
        // without keying this effect on the `items` array identity.
        const currentItems = itemsRef.current;
        const cartProductIds = new Set(currentItems.map((i) => i.productId));
        const excludeSkuIds = currentItems.map((i) => i.vendorSkuId);
        const load = async () => {
            setIsLoadingVendor(true);
            try {
                const rows = await getProductsByVendor(mostFrequentVendorId, {
                    limit: VENDOR_RAIL_FETCH_LIMIT,
                    excludeSkuIds,
                });
                if (!cancelled) {
                    setVendorRail(reshapeVendorSkus(rows, cartProductIds));
                }
            } catch {
                /* notification layer surfaces errors */
            } finally {
                if (!cancelled) setIsLoadingVendor(false);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [mostFrequentVendorId, relatedProductIdsKey, getProductsByVendor]);

    // TODO(backend): /shoppingcarts payload has no image URL and no vendor stock
    // (#443) — hydrate by fetching each product. Backend should embed both in the
    // cart eventually.
    const {
        products,
        failed: failedProductReads,
        retryFailed: retryFailedProductReads,
    } = useCartProducts(
        items.map((item) => item.productId),
    );

    const itemCount = items.length;
    // #228 / E4-S12 (Lead ruling). The subtotal — and with it the free-shipping meter, which
    // reads the same figure — covers only the lines this destination can receive. It is what
    // the shopper would pay once the restricted lines are gone, NOT what they are about to be
    // charged: CreateOrderCommand refuses the entire order with a 412 while any restricted
    // line is in the cart. RestrictedLinesNotice carries that point in words, and the count
    // rendered beside the figure counts the same lines the figure prices.
    // PO ruling 2026-11-02: a line no live vendor offers is left out the same way — its
    // price is the dead offer's (offeredLines).
    const restrictedCount = restrictedLineCount(items);
    const pricedItems = offeredLines(items, products);
    const subtotal = shippableSubtotal(pricedItems);
    const subtotalUnitCount = shippableUnitCount(pricedItems);
    // #429. With no public payment keys checkout can place no order, so both Checkout
    // controls close here — the same predicate Checkout's Place Order gates on.
    const checkoutUnavailable = !isCardPaymentConfigured();
    // #443 / C-07. Each line's vendor stock, where the product read says it, and the
    // lines above it: checkout refuses the whole order (412) while there is one, so
    // both Checkout controls close, as they do for a restricted line.
    const { stocks, overStockCount } = cartStock(items, products);
    // PO ruling 2026-11-02: a line no live vendor offers can't be ordered, so it
    // closes both Checkout controls too (from the same product reads).
    const noOfferCount = noOfferLineCount(items, products);
    // THE rule both Checkout controls (the summary's and the mobile one) close on.
    const isCheckoutClosed =
        restrictedCount > 0 ||
        checkoutUnavailable ||
        overStockCount > 0 ||
        noOfferCount > 0;

    if (readStatus === "loading" && !shoppingCart) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <Spinner size="lg" />
            </div>
        );
    }

    // Whether or not a cart is in hand. This page reads its cart on mount, on
    // Retry, and again when the read itself changes: a new destination (or
    // session). A cart in hand when that read fails is the previous
    // destination's, isRestricted flags and all, or one this page never
    // confirmed, and must not be shown as the current one (PR 374 review 10).
    if (readStatus === "failed") {
        return (
            <div className="bg-base-200 text-neutral min-h-[60vh]">
                <div className="max-w-6xl mx-auto px-4 py-4 md:py-6">
                    <ErrorState onRetry={fetchCart} />
                </div>
            </div>
        );
    }

    return (
        <div className="bg-base-200 text-neutral">
            <div className="max-w-6xl mx-auto px-4 py-4 md:py-6 text-left">
                <header className="mb-4">
                    <h1 className="text-2xl md:text-3xl font-bold text-neutral">
                        Cart
                    </h1>
                </header>

                {itemCount === 0 ? (
                    <p className="mt-8 text-neutral/70">Your cart is empty.</p>
                ) : (
                    <div className="grid gap-4 lg:gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
                        <aside className="lg:order-2 lg:sticky lg:top-4 self-start">
                            <OrderSummary
                                subtotal={subtotal}
                                itemCount={subtotalUnitCount}
                                excludedCount={restrictedCount}
                                checkoutUnavailable={checkoutUnavailable}
                                overStockCount={overStockCount}
                                noOfferCount={noOfferCount}
                                checkoutClosed={isCheckoutClosed}
                                freeShippingThreshold={
                                    shippingPolicy.freeThreshold
                                }
                            />
                        </aside>

                        <section className="lg:order-1 bg-base-100 rounded-lg p-2 md:p-3">
                            {/* One explanation above the list, not per line. The
                                destination banner offers "Change location", which
                                for a member on no location opens onto an empty
                                picker — the note names the real way out (USNara
                                support, #453). A member on no location is told even with
                                nothing restricted: they can place no order at all,
                                and the controls below key on restrictedCount alone,
                                so otherwise the first they would hear of it is a
                                disabled Place Order at checkout. */}
                            {(restrictedCount > 0 || hasNoLocationMembership) && (
                                <div className="mb-3">
                                    {hasNoLocationMembership ? (
                                        <NoLocationNote
                                            message={
                                                restrictedCount > 0
                                                    ? NO_LOCATION_CART_NOTE
                                                    : NO_LOCATION_ORDER_BLOCKED
                                            }
                                        />
                                    ) : (
                                        <RestrictedLinesNotice
                                            count={restrictedCount}
                                        />
                                    )}
                                </div>
                            )}

                            <div>
                                {items.map((item, index) => {
                                    const product = products.get(item.productId);
                                    const stock = stocks[index];
                                    const quantityRefusal = quantityRefusals[item.id];
                                    const switchRefusal = refusals[item.id];
                                    // #443 round 4: a line whose own vendor no longer
                                    // offers it may move to one that can take it.
                                    const alternative = alternativeVendor(
                                        item,
                                        product,
                                        items,
                                    );
                                    const refusalAction = (
                                        step: RefusalNextStep | undefined,
                                    ): LineRefusalAction | undefined =>
                                        step?.kind === "remove"
                                            ? {
                                                kind: "remove",
                                                onRemove: () => removeItem(item),
                                            }
                                            : step?.kind === "switch"
                                                ? {
                                                    kind: "switch",
                                                    vendor: step.vendor,
                                                    onSwitch: (vendor) =>
                                                        switchLine(item, vendor),
                                                    disabled: isSwitchHeld,
                                                }
                                                : step?.kind === "lowerToSwitch"
                                                    ? {
                                                        kind: "lowerToSwitch",
                                                        vendor: step.vendor,
                                                        lowerTo: step.lowerTo,
                                                        onRemove: () => removeItem(item),
                                                    }
                                                    : undefined;
                                    return (
                                        <CartItemRow
                                            key={item.id || item.vendorSkuId}
                                            item={item}
                                            imageUrl={
                                                product && getProductImage(product)
                                            }
                                            detailsFailed={
                                                !product &&
                                                failedProductReads.has(
                                                    item.productId,
                                                )
                                            }
                                            onRetryDetails={
                                                retryFailedProductReads
                                            }
                                            piecesPerUnit={linePiecesPerUnit(
                                                item,
                                                product,
                                            )}
                                            // #443: the stepper stops at the vendor's
                                            // stock and the API's per-line limit,
                                            // flooring at 1 — the line is fixed by a
                                            // switch or Remove, not by a quantity of 0.
                                            maxQuantity={quantityCap(
                                                stock?.available,
                                            )}
                                            outOfStock={lineHasNoOffers(
                                                item,
                                                product,
                                            )}
                                            notice={
                                                <>
                                                    <LineStockNotice
                                                        quantity={item.quantity}
                                                        stock={stock}
                                                        // The refusal's own switch
                                                        // stands in for this one.
                                                        onSwitch={
                                                            quantityRefusal?.kind ===
                                                            "offering"
                                                                ? undefined
                                                                : (vendor) =>
                                                                    switchLine(
                                                                        item,
                                                                        vendor,
                                                                    )
                                                        }
                                                        switchDisabled={isSwitchHeld}
                                                    />
                                                    {quantityRefusal && (
                                                        <LineRefusalNotice
                                                            message={
                                                                quantityRefusal.message
                                                            }
                                                            action={refusalAction(
                                                                refusalNextStep(
                                                                    quantityRefusal,
                                                                    "quantity",
                                                                    alternative,
                                                                    limitedAlternative(
                                                                        item,
                                                                        product,
                                                                        items,
                                                                    ),
                                                                ),
                                                            )}
                                                        />
                                                    )}
                                                    {switchRefusal && (
                                                        <LineRefusalNotice
                                                            message={
                                                                switchRefusal.message
                                                            }
                                                            action={refusalAction(
                                                                refusalNextStep(
                                                                    switchRefusal,
                                                                    "switch",
                                                                    alternative,
                                                                ),
                                                            )}
                                                        />
                                                    )}
                                                </>
                                            }
                                            onQuantityChange={(next) => {
                                                // A switch refusal answered the old
                                                // quantity; this edit makes it moot.
                                                clearRefusal(item.id);
                                                changeQuantity(item, next);
                                            }}
                                            onRemove={() => removeItem(item)}
                                        />
                                    );
                                })}
                            </div>

                            <div className="mt-3 pt-3 border-t border-base-300">
                                <div className="flex items-baseline justify-end gap-3">
                                    <p className="text-sm text-neutral">
                                        <span className="text-neutral/70">
                                            Subtotal
                                        </span>
                                        <span className="text-neutral/70 ml-1">
                                            ({subtotalUnitCount}{" "}
                                            {subtotalUnitCount === 1
                                                ? "Item"
                                                : "Items"}
                                            )
                                        </span>
                                    </p>
                                    <p className="text-base md:text-lg font-bold text-neutral">
                                        {currencyFormatter.format(subtotal)}
                                    </p>
                                </div>
                                {/* On mobile this figure sits directly above the
                                    Checkout control, so it carries the same caveat
                                    the summary does — and the control is closed for
                                    the same reason the summary's is. */}
                                {restrictedCount > 0 && (
                                    <p
                                        data-testid="cart-subtotal-excluded"
                                        className="mt-1 text-right text-sm text-neutral/70"
                                    >
                                        {excludedCaveat(
                                            restrictedCount,
                                            hasNoLocationMembership,
                                        )}
                                    </p>
                                )}
                                {checkoutUnavailable && (
                                    <p
                                        data-testid="cart-checkout-unavailable"
                                        className="mt-1 text-right text-sm font-semibold text-error lg:hidden"
                                    >
                                        {CHECKOUT_UNAVAILABLE_REASON}
                                    </p>
                                )}
                                {overStockCount > 0 && (
                                    <p
                                        data-testid="cart-over-stock"
                                        className="mt-1 text-right text-sm font-semibold text-error lg:hidden"
                                    >
                                        {overStockReason(overStockCount)}
                                    </p>
                                )}
                                {noOfferCount > 0 && (
                                    <p
                                        data-testid="cart-out-of-stock"
                                        className="mt-1 text-right text-sm font-semibold text-error lg:hidden"
                                    >
                                        {outOfStockReason(noOfferCount)}
                                    </p>
                                )}
                                {isCheckoutClosed ? (
                                    <button
                                        type="button"
                                        disabled
                                        className="btn btn-warning w-full text-neutral font-bold mt-3 lg:hidden"
                                    >
                                        Checkout
                                    </button>
                                ) : (
                                    <Link
                                        to={ROUTES.CHECKOUT}
                                        className="btn btn-warning w-full text-neutral font-bold mt-3 lg:hidden"
                                    >
                                        Checkout
                                    </Link>
                                )}
                            </div>
                        </section>
                    </div>
                )}

                {/* Optional recommendation rail: hidden when it has nothing to recommend, but a failed read is a
                    failed state with Retry, never a hidden or empty rail (#449; supersedes #223's hide-on-failure
                    ruling for this rail). */}
                {itemCount > 0 &&
                    (relatedRail.status !== "loaded" || relatedRail.products.length > 0) && (
                    <ProductCarouselRow
                        title="Related Items"
                        products={relatedRail.products}
                        viewAllHref={ROUTES.BROWSE}
                        isLoading={relatedRail.status === "loading"}
                        hasError={relatedRail.status === "failed"}
                        onRetry={relatedRail.retry}
                        errorMessage="Couldn't load related items."
                    />
                )}

                {/* Optional recommendation rail: hidden on a failed read by design (#223 ruling), not a failed state. */}
                {itemCount > 0 &&
                    mostFrequentVendor &&
                    (isLoadingVendor || vendorRail.length > 0) && (
                    <ProductCarouselRow
                        title={`Shop ${mostFrequentVendor.vendorName}`}
                        products={vendorRail}
                        isLoading={isLoadingVendor}
                    />
                )}
            </div>
        </div>
    );
};
